import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { detectPlatform, isMedianShellUA, parseOsVersion } from "@/lib/platform";
import { reportError } from "@/lib/reportError";

// Every failure exit below reports through `reportError` with a `step` tag.
// Issue #1559: Android Google Sign-In has never once succeeded (auth.sessions,
// all time: 11 iOS-native-shell Google users, 0 Android), and the single Sentry
// event we had said only "Unexpected error during Google Sign-In (legacy
// mode)" — Median's own string, passed through a bare console.error with no
// platform and no indication of WHICH of these exits fired. A user cancelling
// and a misconfigured Android OAuth client produced identical events, and those
// two have opposite fixes. These tags exist to tell them apart.
const STEP = {
  /** Bridge invoked its callback with nothing at all. */
  noResponse: "median-no-response",
  /** Bridge responded and Median's own native sign-in failed. THE OBSERVED ONE. */
  bridgeError: "median-bridge-error",
  /** Bridge responded OK but carried no token in any known shape. */
  noIdToken: "median-no-id-token",
  /** Our Supabase exchange rejected the token (after the nonce retry). */
  supabaseIdToken: "median-supabase-id-token",
  /** Non-Median path: supabase.auth.signInWithOAuth refused to start. */
  webOAuthInit: "web-oauth-init",
} as const;

// Median's status code, as a Sentry TAG. #1705.
//
// It is the one field that separates a user cancelling the account picker from
// a DEVELOPER_ERROR (code 10) — opposite fixes — and as an extra it could not be
// filtered or counted. Tag values must be short, bounded strings, so only a
// code-shaped value becomes the tag; anything else is "unrecognized" and the
// raw value stays in the extras. "unknown" means Median sent no code at all.
function medianStatusCodeTag(response: any): string {
  const raw = response?.statusCode ?? response?.code ?? response?.status;
  if (raw === undefined || raw === null || raw === "") return "unknown";
  const s = String(raw);
  return /^[A-Za-z0-9_.-]{1,32}$/.test(s) ? s : "unrecognized";
}

/** The tags every Median Google failure exit carries. Reporting only. */
function medianFailureTags(response: any, provider: "google" | "apple") {
  return {
    platform: detectPlatform(),
    provider,
    medianStatusCode: medianStatusCodeTag(response),
    os_version: parseOsVersion(
      typeof navigator === "undefined" ? "" : navigator.userAgent
    ),
  };
}

// Google and Apple are both shown to everyone, everywhere (issue #644).
//
// History, because the render condition here has changed twice and the reasons
// are not visible from the code: Google was hidden inside the Median iOS
// wrapper from v1.1 (May 6 2026) because Google 403s any OAuth in a webview
// (disallowed_useragent), and the native-bridge replacement below failed at the
// last step on a Supabase nonce error we had no dashboard access to fix on
// Lovable Cloud. #302/#318 fixed the flow and re-revealed the button, but only
// behind a device-local 5-tap-logo test gate, pending a device test. That test
// passed on Aug 6 2026, so the gate is gone and iOS users see Google again.
//
// The Median-vs-browser split still exists in handleOAuth below — it decides
// which *mechanism* signs you in (native bridge vs. webview redirect), which is
// a different question from whether the button renders. Don't reintroduce a
// visibility branch here.

// Inline JWT payload decoder. No signature verification — Supabase validates
// the token server-side. Used only to read the `nonce` claim as a diagnostic
// fallback when Median's bridge response doesn't surface the raw nonce.
function extractNonceFromJwt(token: string): string | undefined {
  try {
    const payload = token.split(".")[1];
    if (!payload) return undefined;
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof decoded?.nonce === "string" ? decoded.nonce : undefined;
  } catch {
    return undefined;
  }
}

const SocialAuthButtons = () => {
  const [loading, setLoading] = useState<"google" | "apple" | null>(null);

  // ⚠️ TEMPORARY AND REVERSIBLE — hide Google inside the Median ANDROID shell.
  // Issue #1700.
  //
  // Google sign-in has never once succeeded in the Android app: auth.sessions,
  // all time, 11 iOS-native-shell Google users and 0 Android. The cause is
  // Google Cloud configuration rather than this code — no Android-type OAuth
  // client registered for com.kinyoudoitall.kin — so the native bridge below
  // calls back with "No credentials available", and she gets a red toast
  // carrying that raw Android SDK string. Every retry fails identically. An
  // absent button beats a dead end that is 100% reproducible.
  //
  // iOS ran this exact interim pattern from May 6 to Jul 24 2026 (023dfde2 added
  // the gate, e48219c6 deleted it once a device test passed). It is a hide, not
  // a fix, and it is meant to come out. Concretely:
  //
  // ── DELETE `hideGoogle` AND RESTORE THE BUTTON WHEN BOTH ARE TRUE ─────────
  //   1. An Android-type OAuth client exists in Google Cloud for
  //      com.kinyoudoitall.kin carrying the PLAY APP-SIGNING SHA-1 — Play
  //      Console → Setup → App integrity → App signing key certificate. NOT the
  //      upload key certificate shown beside it; confusing the two reproduces
  //      this same failure.
  //   2. A real Android device completes Google sign-in end to end.
  // Both, not either. A console change alone does not earn the restore.
  //
  // ⚠️ As of Sep 23 2026 no OPEN issue tracks that config work. #1559 was closed
  // COMPLETED when its phase-1 Sentry tagging merged (#1560); the SHA-1 analysis
  // survives only as a comment on it. Do not read that closed issue as evidence
  // the config is fixed — reopen it or file fresh before restoring.
  //
  // Scope is deliberate and both halves are load-bearing. `detectPlatform() ===
  // "android"` alone is TRUE for Android mobile web (platform.ts), which uses
  // the signInWithOAuth path below and works today — a Google signup came
  // through Chrome on Sep 19 2026. `isMedianShellUA` alone would also catch the
  // iOS shell, where Google is device-verified working. Widening either half
  // breaks a path that currently works.
  const hideGoogle =
    typeof navigator !== "undefined" &&
    isMedianShellUA(navigator.userAgent) &&
    detectPlatform() === "android";

  const handleOAuth = async (provider: "google" | "apple") => {
    setLoading(provider);

    // In the Median wrapper, route Google through the native Social Login
    // bridge rather than the webview OAuth redirect, which Google 403s
    // (disallowed_useragent). The nonce chain + retry below handles Supabase's
    // signInWithIdToken nonce requirement.
    //
    // ⚠️ This branch has NO platform check, so Android Median users take it
    // too. This comment used to say "native Google iOS SDK", which read as if
    // the branch were iOS-only and is part of why nobody noticed that Android
    // Google Sign-In has never worked (#1559). The bridge must still be a
    // callable function to enter — that half is what lets us call `.login`.
    if (
      provider === "google" &&
      typeof navigator !== "undefined" &&
      isMedianShellUA(navigator.userAgent) &&
      typeof (window as any).median?.socialLogin?.google?.login === "function"
    ) {
      (window as any).median.socialLogin.google.login({
        callback: async (response: any) => {
          if (!response) {
            reportError("Median Google login returned no response", null, {
              tags: { step: STEP.noResponse, ...medianFailureTags(response, provider) },
            });
            toast({
              title: "Sign-in failed",
              description: "No response from Google sign-in. Please try again.",
              variant: "destructive",
            });
            setLoading(null);
            return;
          }

          if (response.error) {
            reportError("Median Google login error", response.error, {
              tags: { step: STEP.bridgeError, ...medianFailureTags(response, provider) },
              extra: {
                // Same as the missing-idToken exit: tells us whether Median's
                // error arrives as a bare string or inside a richer object.
                responseKeys: Object.keys(response ?? {}),
                // Median's native layer is the only thing that knows whether
                // this was a cancel or a DEVELOPER_ERROR, so carry everything
                // it gave us rather than only the message.
                medianError: response.error,
                medianStatusCode:
                  response?.statusCode ?? response?.code ?? response?.status,
                // ⚠️ UNVERIFIED (#1705): nothing in this repo or a seen event
                // establishes that Median exposes `.version` at either path.
                // It may be undefined on every event. Don't read its absence
                // as "old binary", and don't guess another path here.
                medianAppVersion:
                  (window as any).median?.version ??
                  (window as any).gonative?.version,
                userAgent:
                  typeof navigator === "undefined" ? "" : navigator.userAgent,
              },
            });
            toast({
              title: "Sign-in failed",
              description:
                typeof response.error === "string"
                  ? response.error
                  : "Please try again.",
              variant: "destructive",
            });
            setLoading(null);
            return;
          }

          const idToken =
            response?.idToken ??
            response?.id_token ??
            response?.userDetails?.idToken;

          if (!idToken) {
            reportError("Median Google response missing idToken", null, {
              tags: { step: STEP.noIdToken, ...medianFailureTags(response, provider) },
              extra: { responseKeys: Object.keys(response ?? {}) },
            });
            toast({
              title: "Sign-in failed",
              description:
                "Google sign-in didn't return a token. Please try again.",
              variant: "destructive",
            });
            setLoading(null);
            return;
          }

          let nonce: string | undefined =
            response?.nonce ??
            response?.rawNonce ??
            response?.userDetails?.nonce;

          let extractedFromJwt = false;
          if (!nonce) {
            const fromJwt = extractNonceFromJwt(idToken);
            if (fromJwt) {
              nonce = fromJwt;
              extractedFromJwt = true;
            }
          }

          const noncePath: string = response?.nonce
            ? "response.nonce"
            : response?.rawNonce
            ? "response.rawNonce"
            : response?.userDetails?.nonce
            ? "response.userDetails.nonce"
            : extractedFromJwt
            ? "jwt-claim (hashed, likely won't validate)"
            : "none";
          console.log("[median google response]", response, {
            noncePath,
            hasNonce: !!nonce,
          });

          let { error: supabaseError } = await supabase.auth.signInWithIdToken({
            provider: "google",
            token: idToken,
            ...(nonce ? { nonce } : {}),
          });

          if (supabaseError && /nonce/i.test(supabaseError.message ?? "")) {
            console.warn("[median google] retrying signInWithIdToken without nonce");
            const { error: retryError } = await supabase.auth.signInWithIdToken({
              provider: "google",
              token: idToken,
            });
            supabaseError = retryError ?? null;
          }

          if (supabaseError) {
            reportError("Supabase signInWithIdToken error", supabaseError, {
              tags: {
                step: STEP.supabaseIdToken,
                ...medianFailureTags(response, provider),
              },
              extra: { noncePath, hadNonce: !!nonce },
            });
            toast({
              title: "Sign-in failed",
              description: supabaseError.message ?? "Please try again.",
              variant: "destructive",
            });
            setLoading(null);
            return;
          }

          setLoading(null);
        },
      });
      return;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
        skipBrowserRedirect: true,
      },
    });

    if (error) {
      reportError("OAuth error", error, {
        tags: { step: STEP.webOAuthInit, platform: detectPlatform(), provider },
      });
      toast({
        title: "Sign-in failed",
        description: error.message ?? "Please try again.",
        variant: "destructive",
      });
      setLoading(null);
      return;
    }

    if (data?.url) {
      if (provider === "google") {
        window.open(data.url, "_blank");
      } else {
        window.location.assign(data.url);
      }
    }
  };

  return (
    <div className="w-full mt-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px" style={{ backgroundColor: "rgba(200,200,244,0.3)" }} />
        <span className="font-body font-medium" style={{ fontSize: 13, color: "#54525C" }}>
          Or
        </span>
        <div className="flex-1 h-px" style={{ backgroundColor: "rgba(200,200,244,0.3)" }} />
      </div>

      <div className="flex gap-3">
        {!hideGoogle && (
          <button
            type="button"
            onClick={() => handleOAuth("google")}
            disabled={loading !== null}
            aria-label="Continue with Google"
            className="flex-1 flex items-center justify-center transition-all hover:bg-[#FAFAFC] active:bg-[#F0F0FF] disabled:opacity-60"
            style={{
              height: 48,
              backgroundColor: "#FFFFFF",
              border: "1px solid #E8E6F4",
              borderRadius: 14,
              boxShadow: "0 1px 4px rgba(28,26,62,0.06)",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          </button>
        )}

        <button
          type="button"
          onClick={() => handleOAuth("apple")}
          disabled={loading !== null}
          aria-label="Continue with Apple"
          className="flex-1 flex items-center justify-center transition-all hover:bg-[#1A1A1A] active:bg-[#2A2A2A] disabled:opacity-60"
          style={{
            height: 48,
            backgroundColor: "#000000",
            border: "none",
            borderRadius: 14,
            boxShadow: "0 1px 4px rgba(28,26,62,0.06)",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#FFFFFF">
            <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.43C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SocialAuthButtons;
